import { useState } from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { Button } from '../components/ui/button';
import { Phone, Shield, Clock, Award, ChevronRight, Star, ArrowRight, CheckCircle2 } from 'lucide-react';
import axios from 'axios';
import { API } from '../lib/api';
import { toast } from 'sonner';



const Home = () => {
  const [quoteForm, setQuoteForm] = useState({ name: '', email: '', phone: '', service: '', details: '' });
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleQuoteSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    try {
      await axios.post(`${API}/quote`, quoteForm);
      toast.success('Quote request submitted! We\'ll contact you within 24 hours.');
      setQuoteForm({ name: '', email: '', phone: '', service: '', details: '' });
    } catch (error) {
      toast.error('Failed to submit quote. Please try again.');
    }
    setIsSubmitting(false);
  };

  const services = [
    { title: 'Framing', desc: 'Expert structural framing for residential and commercial projects', image: 'https://customer-assets-v7afamib.emergentagent.net/job_05714f6e-3774-4985-88d6-8908b452b8f7/artifacts/57ch736f_IMG_8182.webp' },
    { title: 'Drywall & Taping', desc: 'Professional drywall installation with flawless finishing', image: 'https://images.unsplash.com/photo-1768321917806-b4b6fbbef837?w=600&h=400&fit=crop' },
    { title: 'Painting', desc: 'Interior and exterior painting with premium materials', image: 'https://images.unsplash.com/photo-1745092707630-c00ef0a006c4?w=600&h=400&fit=crop' },
  ];

  const whyChooseUs = [
    { icon: Shield, title: 'Licensed & Insured', desc: 'Full coverage for your peace of mind' },
    { icon: Award, title: '15+ Years Experience', desc: 'Proven expertise in construction' },
    { icon: Clock, title: 'On-Time Delivery', desc: 'We respect your timeline' },
    { icon: CheckCircle2, title: 'Locally Owned & Operated', desc: 'Proudly serving BC communities' },
  ];

  const testimonials = [
    { name: 'Michael T.', role: 'Property Developer', text: 'Exceptional quality and on-time delivery. Highly recommend!', rating: 5 },
    { name: 'Sarah W.', role: 'Homeowner', text: 'Transformed our home beautifully. Professional team!', rating: 5 },
    { name: 'James C.', role: 'General Contractor', text: 'My go-to subcontractor for framing and finishing.', rating: 5 },
  ];

  const galleryImages = [
    'https://customer-assets-v7afamib.emergentagent.net/job_05714f6e-3774-4985-88d6-8908b452b8f7/artifacts/oqkxk0ok_Screenshot%202026-08-24%20at%2011.16.10%E2%80%AFAM.webp',
    'https://customer-assets-v7afamib.emergentagent.net/job_05714f6e-3774-4985-88d6-8908b452b8f7/artifacts/d2lve5hb_IMG_8179.webp',
    'https://customer-assets.emergentagent.com/job_05714f6e-3774-4985-88d6-8908b452b8f7/artifacts/z7ytpe7v_IMG_6805.jpg',
    'https://customer-assets-v7afamib.emergentagent.net/job_05714f6e-3774-4985-88d6-8908b452b8f7/artifacts/iy45swsg_IMG_8180.webp',
    '/sicamous_apt_1.jpg',
  ];

  return (
    <div data-testid="home-page">
      <Helmet>
        <title>S&S Group Services | BC's Trusted Construction Company | Vernon, BC</title>
        <meta name="description" content="S&S Group Services - Professional construction company in Vernon, BC. Specializing in framing, drywall, painting, siding & residential/commercial builds. Call (236) 338-0659 for a free quote." />
        <meta name="keywords" content="construction company Vernon BC, general contractor BC, framing drywall painting siding, townhome builder Okanagan, residential commercial construction BC" />
        <link rel="canonical" href="https://www.ssgroupservices.ca/" />
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://www.ssgroupservices.ca/" />
        <meta property="og:title" content="S&S Group Services | BC's Trusted Construction Company" />
        <meta property="og:description" content="Professional construction services in British Columbia. Framing, drywall, painting, siding & more. Licensed & insured. Free estimates." />
        <meta property="og:image" content="https://customer-assets-v7afamib.emergentagent.net/job_05714f6e-3774-4985-88d6-8908b452b8f7/artifacts/d7qp46h7_IMG_8178.webp" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="S&S Group Services | BC's Trusted Construction Company" />
        <meta name="twitter:description" content="Framing, drywall, painting, siding & full builds across BC. Free estimates." />
      </Helmet>
      {/* Hero Section */}
      <section className="relative min-h-[90vh] flex items-center" data-testid="hero-section">
        <div 
          className="absolute inset-0 bg-cover bg-center"
          style={{ backgroundImage: 'url(https://customer-assets-v7afamib.emergentagent.net/job_05714f6e-3774-4985-88d6-8908b452b8f7/artifacts/d7qp46h7_IMG_8178.webp)' }}
        />
        <div className="absolute inset-0 hero-overlay" />
        <div className="absolute inset-0 grid-pattern" />
        
        <div className="container relative z-10">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            {/* Hero Content */}
            <div className="animate-fade-in-up">
              <div className="flex items-center gap-2 mb-6">
                <div className="w-12 h-0.5 bg-blue-500" />
                <span className="text-blue-400 text-sm font-medium uppercase tracking-widest">BC'S TRUSTED CONSTRUCTION COMPANY</span>
              </div>
              <h1 className="heading-xl text-white mb-6" data-testid="hero-heading">
                Building Excellence in Commercial & Residential Construction
              </h1>
              <p className="text-lg text-slate-300 mb-8 max-w-xl">
                Trusted construction experts delivering quality, precision & reliability across British Columbia. From framing to finishing, we build your vision.
              </p>
              <div className="flex flex-wrap gap-4">
                <Link to="/contact">
                  <Button size="lg" className="btn-accent text-base px-8" data-testid="hero-quote-btn">
                    Get a Free Quote
                    <ArrowRight className="w-5 h-5 ml-2" />
                  </Button>
                </Link>
                <a href="tel:+12363380659">
                  <Button size="lg" variant="outline" className="btn-white text-base px-8" data-testid="hero-call-btn">
                    <Phone className="w-5 h-5 mr-2" />
                    Call Now
                  </Button>
                </a>
              </div>
              
              {/* Trust Badges */}
              <div className="flex flex-wrap gap-4 mt-10">
                <div className="flex items-center gap-2 px-4 py-2 bg-white/10 backdrop-blur-sm">
                  <Shield className="w-5 h-5 text-blue-400" />
                  <span className="text-white text-sm font-medium">Licensed & Insured</span>
                </div>
                <div className="flex items-center gap-2 px-4 py-2 bg-white/10 backdrop-blur-sm">
                  <Award className="w-5 h-5 text-blue-400" />
                  <span className="text-white text-sm font-medium">15+ Years Experience</span>
                </div>
                <div className="flex items-center gap-2 px-4 py-2 bg-white/10 backdrop-blur-sm">
                  <CheckCircle2 className="w-5 h-5 text-blue-400" />
                  <span className="text-white text-sm font-medium">Locally Owned & Operated</span>
                </div>
              </div>
            </div>

            {/* Quote Form */}
            <div className="hidden lg:block animate-fade-in-up delay-200">
              <div className="bg-white p-8 shadow-2xl">
                <h3 className="heading-md text-slate-900 mb-2" data-testid="quote-form-title">Get Your Free Quote</h3>
                <p className="text-slate-500 mb-6">Fill out the form and we'll contact you within 24 hours.</p>
                <form onSubmit={handleQuoteSubmit} className="space-y-4" data-testid="hero-quote-form">
                  <input
                    type="text"
                    placeholder="Your Name"
                    className="form-input"
                    value={quoteForm.name}
                    onChange={(e) => setQuoteForm({ ...quoteForm, name: e.target.value })}
                    required
                    data-testid="quote-name-input"
                  />
                  <input
                    type="email"
                    placeholder="Email Address"
                    className="form-input"
                    value={quoteForm.email}
                    onChange={(e) => setQuoteForm({ ...quoteForm, email: e.target.value })}
                    required
                    data-testid="quote-email-input"
                  />
                  <input
                    type="tel"
                    placeholder="Phone Number"
                    className="form-input"
                    value={quoteForm.phone}
                    onChange={(e) => setQuoteForm({ ...quoteForm, phone: e.target.value })}
                    required
                    data-testid="quote-phone-input"
                  />
                  <select
                    className="form-input"
                    value={quoteForm.service}
                    onChange={(e) => setQuoteForm({ ...quoteForm, service: e.target.value })}
                    data-testid="quote-service-select"
                  >
                    <option value="">Select Service</option>
                    <option value="framing">Framing</option>
                    <option value="drywall">Drywall & Taping</option>
                    <option value="painting">Painting</option>
                    <option value="siding">Siding</option>
                    <option value="flooring">Flooring</option>
                    <option value="interior">Interior Finishing</option>
                    <option value="commercial">Commercial Construction</option>
                    <option value="residential">Residential Construction</option>
                  </select>
                  <textarea
                    placeholder="Project Details (Optional)"
                    className="form-input min-h-[100px]"
                    value={quoteForm.details}
                    onChange={(e) => setQuoteForm({ ...quoteForm, details: e.target.value })}
                    data-testid="quote-details-textarea"
                  />
                  <Button 
                    type="submit" 
                    className="w-full btn-accent text-base py-6" 
                    disabled={isSubmitting}
                    data-testid="quote-submit-btn"
                  >
                    {isSubmitting ? 'Submitting...' : 'Request Free Quote'}
                  </Button>
                </form>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Services Preview */}
      <section className="section-padding bg-white" data-testid="services-section">
        <div className="container">
          <div className="text-center mb-16">
            <span className="caption text-blue-600">What We Do</span>
            <h2 className="heading-lg text-slate-900 mt-3 mb-4">Our Construction Services</h2>
            <p className="text-slate-600 max-w-2xl mx-auto">
              From structural framing to interior finishing, we deliver comprehensive construction solutions for commercial and residential projects.
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            {services.map((service, index) => (
              <div 
                key={service.title}
                className="service-card card-hover group"
                style={{ animationDelay: `${index * 100}ms` }}
                data-testid={`service-card-${service.title.toLowerCase().replace(/\s+/g, '-')}`}
              >
                <div className="img-zoom h-48 mb-6">
                  <img src={service.image} alt={service.title} className="w-full h-full object-cover" />
                </div>
                <h3 className="heading-sm text-slate-900 mb-3" style={{ fontFamily: 'Oswald, sans-serif' }}>{service.title}</h3>
                <p className="text-slate-600 mb-4">{service.desc}</p>
                <Link 
                  to="/services" 
                  className="inline-flex items-center text-blue-600 font-semibold text-sm uppercase tracking-wider group-hover:gap-3 gap-2 transition-all"
                >
                  Learn More <ChevronRight className="w-4 h-4" />
                </Link>
              </div>
            ))}
          </div>

          <div className="text-center mt-12">
            <Link to="/services">
              <Button size="lg" className="btn-primary" data-testid="view-all-services-btn">
                View All Services
                <ArrowRight className="w-5 h-5 ml-2" />
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* Why Choose Us */}
      <section className="section-padding bg-slate-50" data-testid="why-choose-section">
        <div className="container">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <div>
              <span className="caption text-blue-600">Why Choose Us</span>
              <h2 className="heading-lg text-slate-900 mt-3 mb-6">Building Trust Through Quality Craftsmanship</h2>
              <p className="text-slate-600 mb-8">
                S&S Group Services is built on a foundation of integrity, skill, and dedication. We understand that your construction project is a significant investment, and we treat it with the respect it deserves.
              </p>
              
              <div className="grid sm:grid-cols-2 gap-6">
                {whyChooseUs.map((item, index) => (
                  <div key={item.title} className="flex items-start gap-4" data-testid={`why-choose-${index}`}>
                    <div className="w-12 h-12 bg-blue-600 flex items-center justify-center flex-shrink-0">
                      <item.icon className="w-6 h-6 text-white" />
                    </div>
                    <div>
                      <h4 className="font-bold text-slate-900 mb-1" style={{ fontFamily: 'Oswald, sans-serif' }}>{item.title}</h4>
                      <p className="text-slate-600 text-sm">{item.desc}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            <div className="relative">
              <div className="grid grid-cols-2 gap-4">
                <img 
                  src="https://customer-assets-v7afamib.emergentagent.net/job_05714f6e-3774-4985-88d6-8908b452b8f7/artifacts/iy45swsg_IMG_8180.webp" 
                  alt="Campfire townhomes exterior finishing" 
                  className="w-full h-64 object-cover"
                />
                <img 
                  src="https://customer-assets-v7afamib.emergentagent.net/job_05714f6e-3774-4985-88d6-8908b452b8f7/artifacts/57ch736f_IMG_8182.webp" 
                  alt="Campfire townhomes framing work" 
                  className="w-full h-64 object-cover mt-8"
                />
              </div>
              <div className="absolute -bottom-6 -left-6 bg-blue-600 text-white p-6">
                <p className="text-4xl font-bold" style={{ fontFamily: 'Oswald, sans-serif' }}>15+</p>
                <p className="text-sm uppercase tracking-wider">Years Experience</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Gallery Preview */}
      <section className="section-padding bg-white" data-testid="gallery-preview-section">
        <div className="container">
          <div className="flex flex-col md:flex-row justify-between items-start md:items-end mb-12">
            <div>
              <span className="caption text-blue-600">Our Work</span>
              <h2 className="heading-lg text-slate-900 mt-3">Recent Projects</h2>
            </div>
            <Link to="/gallery" className="mt-4 md:mt-0">
              <Button variant="outline" className="btn-outline" data-testid="view-gallery-btn">
                View Full Gallery
                <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            </Link>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {galleryImages.map((img, index) => (
              <Link 
                to="/gallery" 
                key={index} 
                className={`gallery-item ${index === 0 ? 'md:col-span-2 md:row-span-2' : ''}`}
                data-testid={`gallery-preview-${index}`}
              >
                <img 
                  src={img} 
                  alt={`Recent construction project ${index + 1} by S&S Group Services BC`} 
                  loading="lazy"
                  className={index === 0 ? 'h-full min-h-[400px]' : 'h-48 md:h-48'}
                />
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* Testimonials */}
      <section className="section-padding bg-slate-900 relative overflow-hidden" data-testid="testimonials-section">
        <div className="absolute inset-0 grid-pattern" />
        <div className="container relative z-10">
          <div className="text-center mb-16">
            <span className="caption text-blue-400">Testimonials</span>
            <h2 className="heading-lg text-white mt-3">What Our Clients Say</h2>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            {testimonials.map((testimonial, index) => (
              <div 
                key={index}
                className="bg-white/5 backdrop-blur-sm border border-white/10 p-8"
                data-testid={`testimonial-${index}`}
              >
                <div className="flex gap-1 mb-4">
                  {[...Array(testimonial.rating)].map((_, i) => (
                    <Star key={i} className="w-5 h-5 fill-yellow-400 text-yellow-400" />
                  ))}
                </div>
                <p className="text-slate-300 mb-6">"{testimonial.text}"</p>
                <div>
                  <p className="text-white font-semibold" style={{ fontFamily: 'Oswald, sans-serif' }}>{testimonial.name}</p>
                  <p className="text-slate-500 text-sm">{testimonial.role}</p>
                </div>
              </div>
            ))}
          </div>

          <div className="text-center mt-12">
            <Link to="/reviews">
              <Button variant="outline" className="border-white text-white hover:bg-white hover:text-slate-900" data-testid="read-reviews-btn">
                Read More Reviews
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="section-padding bg-blue-600 relative" data-testid="cta-section">
        <div className="container relative z-10">
          <div className="max-w-3xl mx-auto text-center">
            <h2 className="heading-lg text-white mb-6">Ready to Start Your Project?</h2>
            <p className="text-blue-100 text-lg mb-8">
              Contact us today for a free consultation and quote. Let's build something great together.
            </p>
            <div className="flex flex-wrap justify-center gap-4">
              <Link to="/contact">
                <Button size="lg" className="btn-white" data-testid="cta-quote-btn">
                  Get a Free Quote
                </Button>
              </Link>
              <a href="tel:+12363380659">
                <Button size="lg" variant="outline" className="border-white text-white hover:bg-white hover:text-blue-600" data-testid="cta-call-btn">
                  <Phone className="w-5 h-5 mr-2" />
                  (236) 338-0659
                </Button>
              </a>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Home;
